import React, { useEffect } from 'react';
import { Typography, Box, Button, Grid, Dialog, DialogContent, useMediaQuery, Select, MenuItem } from '@mui/material';
import CloseModalIcon from '../../../icons/RoundCloseIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { PaymentRequestInfo, getBuildGenericPaymentRequestDto, selectPaymentRequestsState } from '../sagas/paymentrequestslice';
import { useStyles } from '../../../style/Pages/BasicCompanyInfo';
import { InputField } from '../../../components/InputController';
import { useFormik } from 'formik';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { selectBillingState } from '../../billingConfiguration/sagas/billingSlice';
import * as Yup from 'yup';
import RequestDetailsModal from './RequestDetailsModal';
import { EmailRegex } from '../../../utils/regex';

interface NewPaymentRequestModalProps {
  open: boolean;
  onClose: () => void;
}

const schemaValidationPayRequestSetup = Yup.object({
  first_name: Yup.string().required('Required First Name'),
  last_name: Yup.string().required('Required Last Name'),
  payer_email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Required'),
  bank_deposit_uuid: Yup.string().required('Must select deposit account'),
  property_group_uuid: Yup.string().required('Must select Property Group'),
});

type schemaValidationPayRequestSetupType = Yup.InferType<typeof schemaValidationPayRequestSetup>;

const NewPaymentRequestModal = (props: NewPaymentRequestModalProps) => {
  const [selectedDepositBank, setSelectedDepositBank] = React.useState('');
  const [selectedPropertyGroup, setSelectedPropertyGroup] = React.useState('');
  const [openRequestDetails, setOpenRequestDetails] = React.useState(false);
  const { paymentRequestsError } = useSelector(selectPaymentRequestsState);
  const { propertyGroups } = useSelector(selectPropertyGroupDetailsState);
  const { availableBankAccounts } = useSelector(selectBillingState);
  const dispatch = useDispatch();
  const classes = useStyles();

  const payerInitialValues: schemaValidationPayRequestSetupType = {
    first_name: '',
    last_name: '',
    payer_email: '',
    bank_deposit_uuid: '',
    property_group_uuid: '',
  };

  const formikValidator = useFormik<schemaValidationPayRequestSetupType>({
    initialValues: payerInitialValues,
    validationSchema: schemaValidationPayRequestSetup,
    isInitialValid: false, //wtf i have to do this?

    onSubmit: async (values) => {
      //either do create request now, or do create request all in one go at the end.
      return;
    },
  });

  useEffect(() => {
    if (propertyGroups?.length === 1) {
      //if there is only one, then just auto select it
      setSelectedPropertyGroup(propertyGroups[0].Uuid);
      formikValidator.setFieldValue('property_group_uuid', propertyGroups[0].Uuid);
    }
    if (availableBankAccounts?.length === 1) {
      setSelectedDepositBank(availableBankAccounts[0].bankAccountUuid);
      formikValidator.setFieldValue('bank_deposit_uuid', availableBankAccounts[0].bankAccountUuid);
    }
  }, [propertyGroups, availableBankAccounts]);

  const isOnMobileScreen = useMediaQuery('(max-width:600px)');
  const style = {
    height: isOnMobileScreen ? '' : '555px',
  };

  return (
    <React.Fragment>
      <Dialog
        open={props.open}
        onClose={props.onClose}
        fullScreen={isOnMobileScreen}
        BackdropProps={{
          invisible: false,
        }}
        fullWidth={true}
        disableEnforceFocus
        style={{
          background: 'none',
        }}
        PaperProps={{
          style: {
            pointerEvents: 'auto',
            width: '520px',
            borderRadius: '24px',
          },
        }}
      >
        <DialogContent>
          <Box sx={style}>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={2}>
                {' '}
              </Grid>
              <Grid item xs={8}>
                <Typography
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '22px',
                    fontFamily: 'Work Sans',
                  }}
                >
                  Request Details
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <img src={CloseModalIcon} alt="close" style={{ cursor: 'pointer', marginLeft: isOnMobileScreen ? '0px' : '40px' }} onClick={props.onClose} />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                {paymentRequestsError?.message?.length ? (
                  <div className={classes.globalError}>
                    <Typography
                      style={{
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '18px',
                        color: '#DB3939',
                      }}
                    >
                      {paymentRequestsError?.message}
                    </Typography>
                  </div>
                ) : (
                  ''
                )}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              Enter Payer Details:
            </Grid>
            <Grid item xs={12}>
              &nbsp;
            </Grid>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={6}>
                <InputField
                  name="first_name"
                  label="First Name"
                  isError={false}
                  errors={formikValidator.errors?.first_name}
                  touched={formikValidator.touched?.first_name}
                  onBlur={formikValidator.handleBlur}
                  onChange={(e) => formikValidator.setFieldValue('first_name', e.target.value)}
                  value={formikValidator.values.first_name ?? ''}
                />
              </Grid>
              <Grid item xs={6}>
                <InputField
                  name="last_name"
                  label="Last Name"
                  isError={false}
                  errors={formikValidator.errors?.last_name}
                  touched={formikValidator.touched?.last_name}
                  onBlur={formikValidator.handleBlur}
                  onChange={(e) => formikValidator.setFieldValue('last_name', e.target.value)}
                  value={formikValidator.values.last_name ?? ''}
                />
              </Grid>
              <Grid item xs={12}>
                <InputField
                  name="email"
                  label="Email"
                  isError={false}
                  errors={formikValidator.errors?.payer_email}
                  touched={formikValidator.touched?.payer_email}
                  onBlur={formikValidator.handleBlur}
                  onChange={(e) => formikValidator.setFieldValue('payer_email', e.target.value)}
                  value={formikValidator.values.payer_email ?? ''}
                />
              </Grid>
            </Grid>
            {propertyGroups?.length > 1 && (
              <>
                <Grid item xs={12}>
                  Select Property Group:
                </Grid>

                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={12}>
                    <Select
                      value={selectedPropertyGroup ?? ''}
                      displayEmpty
                      style={{ borderRadius: '24px', height: '42px' }}
                      onChange={(e) => {
                        setSelectedPropertyGroup(e.target.value);
                        formikValidator.setFieldValue('property_group_uuid', e.target.value);
                      }}
                    >
                      <MenuItem disabled value={''}>
                        <Typography>Select Property Group</Typography>
                      </MenuItem>
                      {propertyGroups.map((pg) => (
                        <MenuItem value={pg.Uuid} key={pg.Uuid} selected={pg.Uuid === selectedPropertyGroup}>
                          <Typography>{pg.Description}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              </>
            )}
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12}>
                &nbsp;
              </Grid>
            </Grid>
            <Grid item xs={12}>
              Select Deposit Bank Account:
            </Grid>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12}>
                <Select
                  value={selectedDepositBank ?? ''}
                  displayEmpty
                  style={{ borderRadius: '24px', height: '42px' }}
                  onChange={(e) => {
                    setSelectedDepositBank(e.target.value);
                    formikValidator.setFieldValue('bank_deposit_uuid', e.target.value);
                  }}
                >
                  {availableBankAccounts?.length > 1 && (
                    <MenuItem disabled value={''}>
                      <Typography>Select Bank Account</Typography>
                    </MenuItem>
                  )}
                  {availableBankAccounts.map((bank) => (
                    <MenuItem value={bank.bankAccountUuid} key={bank.bankAccountUuid} selected={bank.bankAccountUuid === selectedDepositBank}>
                      <Typography>****{bank.last4}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>

            <Grid item sx={{ position: 'absolute', bottom: 15, left: '190px' }} ml={20}>
              <Button
                variant="contained"
                style={{
                  width: '139px',
                  height: '42px',
                  borderRadius: '24px',
                  textTransform: 'capitalize',
                  color: 'white',
                  backgroundColor: !formikValidator.isValid ? 'rgba(211, 211, 211, 1)' : '#39A2DB',
                  borderColor: '#39A2DB',
                }}
                disabled={!formikValidator.isValid}
                onClick={() => {
                  setOpenRequestDetails(true);
                  dispatch({
                    type: getBuildGenericPaymentRequestDto.type,
                    payload: {
                      PayeeUuid: selectedPropertyGroup,
                      DepositBankAccountUuid: selectedDepositBank,
                      IsPayerGeneric: true,
                      PayerInfo: {
                        Email: formikValidator.values.payer_email,
                        FirstName: formikValidator.values.first_name,
                        LastName: formikValidator.values.last_name,
                      },
                    } as PaymentRequestInfo,
                  });
                }}
                id="continuePayerBtn"
              >
                continue
              </Button>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
      <RequestDetailsModal
        open={openRequestDetails}
        propertyGroupUuid={selectedPropertyGroup}
        isInvisible={true}
        onClose={() => {
          props.onClose();
          setOpenRequestDetails(false);
        }}
        onGoBack={() => {
          setOpenRequestDetails(false);
        }}
      />
    </React.Fragment>
  );
};

export default NewPaymentRequestModal;
